/**
 * Bank Balance Breakdown diagnostic card (Fix 1).
 *
 * Explains the "All Entities" dashboard Bank Balance stat card, which is off
 * by roughly 3x. Reproduces the current computation verbatim (the card's
 * grand total) and decomposes it so the operator can see which cause drives
 * the inflation and by how much:
 *   (a) native non-GBP balances raw-summed as GBP (no FX);
 *   (b) duplicate account docs counted more than once;
 *   (c) out-of-scope / unassigned accounts leaking into the entities total.
 *
 * Read-only. Cross-check: current_card.grand_total must equal the number the
 * card displayed pre-fix.
 */

import { useState } from 'react';
import { Loader2, Search, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtTs(iso) {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

function NativeMap(props) {
  var m = props.map || {};
  var keys = Object.keys(m);
  if (keys.length === 0) return <span className="text-muted-foreground">--</span>;
  return (
    <span className="font-mono">
      {keys.map(function (k, i) {
        return (
          <span key={k}>
            {i > 0 ? ', ' : ''}{m[k]} {k}
          </span>
        );
      })}
    </span>
  );
}

export function BankBalanceBreakdownPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);

  var load = async function () {
    setLoading(true);
    try {
      var res = await diagnosticsAPI.bankBalanceBreakdown();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Bank Balance Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Reproduces the All Entities Bank Balance card total verbatim and decomposes
          it: FX raw-summing (native non-GBP added as GBP), duplicate account docs, and
          out-of-scope / unassigned leakage. Read-only. The reproduced card total must
          equal what the card displayed pre-fix.
        </p>

        <Button onClick={load} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Run Diagnostic
        </Button>

        {data && (
          <div className="space-y-4">
            {/* Headline totals */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">Card total (current, raw-sum)</p>
                <p className="text-xl font-bold font-mono">{data.current_card.grand_total}</p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  scope: {data.current_card.scope}; EUR/DKK added as GBP
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">FX-correct (entities, GBP)</p>
                <p className="text-xl font-bold font-mono">{data.corrected.fx_correct_gbp}</p>
                <p className="text-[10px] text-muted-foreground mt-1">converted per currency at spot</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">Deduped + FX-correct (GBP)</p>
                <p className="text-xl font-bold font-mono">{data.corrected.deduped_fx_correct_gbp}</p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  duplicate impact removed: {data.corrected.duplicate_gbp_impact}
                </p>
              </div>
            </div>

            {data.corrected.unconvertible_currencies.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                <span>Unconvertible currencies (no FX rate): {data.corrected.unconvertible_currencies.join(', ')}</span>
              </div>
            )}

            <p className="text-[10px] text-muted-foreground font-mono">
              computation path: {data.current_computation_path}
            </p>

            {/* Totals blocks */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <p className="text-xs font-medium mb-1">By currency (all visible)</p>
                <div className="text-xs space-y-0.5">
                  {Object.keys(data.totals_by_currency).map(function (c) {
                    return (
                      <div key={c} className="flex justify-between border-b border-border/40 py-0.5">
                        <span>{c}</span><span className="font-mono">{data.totals_by_currency[c]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium mb-1">By jurisdiction</p>
                <div className="text-xs space-y-0.5">
                  {Object.keys(data.totals_by_jurisdiction).map(function (j) {
                    return (
                      <div key={j} className="flex justify-between border-b border-border/40 py-0.5">
                        <span className={j === 'unassigned' ? 'text-amber-600' : ''}>{j}</span>
                        <span className="font-mono">{data.totals_by_jurisdiction[j].gbp_equivalent} GBP</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium mb-1">By entity</p>
                <div className="text-xs space-y-0.5">
                  {Object.keys(data.totals_by_entity).map(function (e) {
                    return (
                      <div key={e} className="flex justify-between border-b border-border/40 py-0.5">
                        <span className={e === 'unassigned' ? 'text-amber-600' : ''} title={e}>
                          {data.totals_by_entity[e].entity_name}
                        </span>
                        <span className="font-mono">{data.totals_by_entity[e].gbp_equivalent} GBP</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Per-account table */}
            <div className="overflow-x-auto">
              <p className="text-xs font-medium mb-1">{data.account_count} account(s)</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1 pr-2 font-medium">Account</th>
                    <th className="py-1 pr-2 font-medium">Source</th>
                    <th className="py-1 pr-2 font-medium">Entity</th>
                    <th className="py-1 pr-2 font-medium">Juris.</th>
                    <th className="py-1 pr-2 font-medium text-right">Native</th>
                    <th className="py-1 pr-2 font-medium text-right">FX</th>
                    <th className="py-1 pr-2 font-medium text-right">GBP</th>
                    <th className="py-1 pr-2 font-medium">Synced</th>
                    <th className="py-1 pr-2 font-medium">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map(function (a) {
                    return (
                      <tr key={a.account_id} className="border-b border-border/40">
                        <td className="py-1 pr-2 truncate max-w-[160px]" title={a.display_name}>{a.display_name || a.account_id}</td>
                        <td className="py-1 pr-2">{a.source}</td>
                        <td className="py-1 pr-2">
                          <span className={a.entity_resolved ? '' : 'text-amber-600'} title={'raw: ' + String(a.raw_entity_id)}>
                            {a.entity_name}
                          </span>
                        </td>
                        <td className="py-1 pr-2">
                          <span className={a.jurisdiction === 'unassigned' ? 'text-amber-600' : ''} title={a.jurisdiction_derived_from}>
                            {a.jurisdiction}
                          </span>
                        </td>
                        <td className="py-1 pr-2 text-right font-mono">{a.native_balance} {a.native_currency}</td>
                        <td className="py-1 pr-2 text-right font-mono">{a.fx_used_gbp == null ? '--' : a.fx_used_gbp}</td>
                        <td className="py-1 pr-2 text-right font-mono">{a.gbp_equivalent == null ? '--' : a.gbp_equivalent}</td>
                        <td className="py-1 pr-2 whitespace-nowrap text-muted-foreground">{fmtTs(a.last_balance_sync)}</td>
                        <td className="py-1 pr-2">
                          {!a.in_entities_scope && <Badge variant="outline" className="text-[9px] mr-1">out-of-scope</Badge>}
                          {a.duplicate && <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-[9px]">dup</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {Object.keys(data.duplicate_groups).length > 0 && (
              <div className="text-xs">
                <p className="font-medium text-red-600 mb-1">
                  Duplicate real-accounts (key: {data.group_key_scheme})
                </p>
                {Object.keys(data.duplicate_groups).map(function (gk) {
                  return (
                    <div key={gk} className="font-mono text-[11px] text-muted-foreground truncate" title={gk}>
                      {gk}: {data.duplicate_groups[gk]} docs
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
